import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from './SearchIcon';

/**
 * One box for finding a task by its key or its words. Submitting goes to the results page
 * rather than showing a dropdown, so a search is a place you can link to, come back to and
 * refine.
 */
export function SearchBox() {
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // "/" focuses it, the convention in every tracker — unless you are already typing somewhere.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active as HTMLElement | null)?.isContentEditable;
      if (typing) return;

      event.preventDefault();
      input.current?.focus();
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <form
      className="search-box"
      onSubmit={(event) => {
        event.preventDefault();
        if (query.trim().length < 2) return;
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }}
    >
      <span className="search-icon">
        <SearchIcon />
      </span>
      <input
        ref={input}
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => event.key === 'Escape' && input.current?.blur()}
        placeholder="Search tasks"
        aria-label="Search tasks, projects and companies"
      />
      {query.length === 0 && <span className="search-hint">/</span>}
    </form>
  );
}
